import Dialog from "@mui/material/Dialog";
import {
	useDeleteShoppingCartTemplateMutation,
	useLazyGetCartTemplatesQuery
} from "features/MarketingMaterials/api/merchProductsApi.ts";
import { Button, type DialogProps, Skeleton } from "@mui/material";
import AutoComplete from "components/Common/Autocomplete/Autocomplete.tsx";
import { useEffect, useMemo, useState } from "react";
import Close from "@mui/icons-material/Close";
import {
	CartTemplateModalBody,
	CartTemplateModalButtons,
	CartTemplateModalHeader,
	DeleteTemplateButton
} from "features/MarketingMaterials/pages/Checkout/components/ViewCartModals/CartTemplateModal.styles.ts";
import DeleteIcon from "@mui/icons-material/Delete";

type CartTemplateOption = {
	draftOrderId?: string | null;
	draftName?: string | null;
};

export interface LoadCartTemplateModalProps {
	isOpen: boolean;
	onClose: () => void;
	onSubmit: (templateId: string) => Promise<void>;
}

export const LoadCartTemplateModal = ({ isOpen, onClose, onSubmit }: LoadCartTemplateModalProps) => {
	const [selectedTemplate, setSelectedTemplate] = useState<CartTemplateOption | null>(null);
	const [trigger, { data: templates, isLoading, isFetching }] = useLazyGetCartTemplatesQuery();
	const [deleteTemplate, { isLoading: isDeletingTemplate }] = useDeleteShoppingCartTemplateMutation();

	const templateOptions = useMemo(() => {
		return (templates ?? []).filter((template): template is CartTemplateOption => Boolean(template?.draftOrderId));
	}, [templates]);

	useEffect(() => {
		if (isOpen) {
			trigger();
		}
	}, [isOpen, trigger]);

	useEffect(() => {
		const [firstTemplate] = templateOptions;
		if (!firstTemplate) {
			if (selectedTemplate) {
				setSelectedTemplate(null);
			}
			return;
		}

		const selectedStillExists = templateOptions.some(
			(template) => template.draftOrderId === selectedTemplate?.draftOrderId
		);

		if (!selectedTemplate || !selectedStillExists) {
			setSelectedTemplate(firstTemplate);
		}
	}, [selectedTemplate, templateOptions]);

	const handleDialogClose: DialogProps["onClose"] = (_event, reason) => {
		if (reason === "backdropClick") return;
		handleClose();
	};

	const handleClose = () => {
		setSelectedTemplate(null);
		onClose();
	};

	const handleRemove = async () => {
		if (selectedTemplate?.draftOrderId) {
			await deleteTemplate({ templateId: selectedTemplate.draftOrderId }).unwrap();
			setSelectedTemplate(null);
			await trigger().unwrap();
		}
	};

	const handleSubmit = async () => {
		if (!selectedTemplate?.draftOrderId) {
			return;
		}

		await onSubmit(selectedTemplate.draftOrderId);
		handleClose();
	};

	return (
		<Dialog
			open={isOpen}
			onClose={handleDialogClose}
			fullWidth
			maxWidth="sm"
		>
			<CartTemplateModalHeader>
				<div>
					<h6>Load a Cart</h6>
					<p>Select a cart to load. This will replace the current contents of your cart.</p>
				</div>
				<Close onClick={handleClose} />
			</CartTemplateModalHeader>
			<CartTemplateModalBody isError={false}>
				{isLoading || isFetching ? (
					<Skeleton
						variant="rectangular"
						height={40}
						sx={{ mt: 1 }}
					/>
				) : (
					<AutoComplete
						value={selectedTemplate}
						label="Cart"
						onChange={(_event, value) => setSelectedTemplate(value)}
						getOptionLabel={(option: CartTemplateOption) => option?.draftName ?? "Untitled cart"}
						isOptionEqualToValue={(option, value) => option?.draftOrderId === value?.draftOrderId}
						options={templateOptions}
						disableClearable
						dataTestId="load-cart-template-selection"
						errorText={undefined}
						noOptionsText="No carts found"
					/>
				)}
				<CartTemplateModalButtons>
					<DeleteTemplateButton
						variant="outlined"
						startIcon={<DeleteIcon />}
						onClick={handleRemove}
						disabled={!selectedTemplate?.draftOrderId || isLoading || isFetching || isDeletingTemplate}
					>
						Remove
					</DeleteTemplateButton>
					<Button
						variant="outlined"
						onClick={handleClose}
					>
						Cancel
					</Button>
					<Button
						variant="contained"
						onClick={handleSubmit}
						disabled={!selectedTemplate?.draftOrderId || isLoading || isFetching || isDeletingTemplate}
					>
						Load Cart
					</Button>
				</CartTemplateModalButtons>
			</CartTemplateModalBody>
		</Dialog>
	);
};
